import React from 'react';
import { BarcodePatient, formatMedicationName, MedicationItem } from './barcode-modal.types';

interface BarcodePrintPreviewProps {
  selectedPrimary: MedicationItem[];
  selectedDistractors: MedicationItem[];
  patient: BarcodePatient;
  renderBarcode: (value: string, className: string) => React.ReactNode;
  renderDataMatrix: (value: string, className: string) => React.ReactNode;
}

export const BarcodePrintPreview: React.FC<BarcodePrintPreviewProps> = ({
  selectedPrimary,
  selectedDistractors,
  patient,
  renderBarcode,
  renderDataMatrix
}) => {
  const hasAllergies = !!patient.allergies && patient.allergies !== 'NA';
  const punchHoles = Array.from({ length: 7 });

  return (
  <div className="barcode-print" id="barcode-print-area">
    <div className="barcode-print__page">
      <div className="barcode-print__content">
        {/* Left: medication labels */}
        <div className={`barcode-print__labels${hasAllergies ? ' barcode-print__labels--with-allergy' : ''}`}>
          {selectedPrimary.length > 0 && (
            <>
              <div className="barcode-print__section-title">Labels for Medications Added to MAR in Simulation Timeline</div>
              {selectedPrimary.map((item) => (
                <div key={`print-${item.id}`} className="barcode-print__label">
                  <div className="barcode-print__med-name">{formatMedicationName(item)}</div>
                  <div className="barcode-print__med-dose">{item.dose}</div>
                  <div className="barcode-print__label-barcode">
                    {renderBarcode(item.barcodeValue, 'barcode-print__barcode-svg')}
                    <div className="barcode-print__barcode-text">{item.barcodeValue}</div>
                  </div>
                </div>
              ))}
            </>
          )}

          {selectedDistractors.length > 0 && (
            <>
              <div className="barcode-print__section-title barcode-print__section-title--distractor">Labels for Distracter Medications</div>
              {selectedDistractors.map((item) => (
                <div key={`print-d-${item.id}`} className="barcode-print__label">
                  <div className="barcode-print__med-name">{formatMedicationName(item)}</div>
                  <div className="barcode-print__med-dose">{item.dose}</div>
                  <div className="barcode-print__label-barcode">
                    {renderBarcode(item.barcodeValue, 'barcode-print__barcode-svg')}
                    <div className="barcode-print__barcode-text">{item.barcodeValue}</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Right: vertical patient wristband */}
        <div className="barcode-print__wristband">
          {/* Top strap with clip hole */}
          <div className="barcode-print__band-strap-top">
            <div className="barcode-print__band-clip-hole" />
          </div>
          {/* Connector from strap to wider tag */}
          <div className="barcode-print__band-connector" />
          {/* Info tag */}
          <div className="barcode-print__band-tag">
            <div className="barcode-print__band-info">
              <span className="barcode-print__band-text barcode-print__band-text--bold">{patient.name}</span>
              <span className="barcode-print__band-text barcode-print__band-text--bold">{patient.age} Y</span>
              {patient.dateOfBirth && <span className="barcode-print__band-text barcode-print__band-text--bold">{patient.dateOfBirth} DOB</span>}
              <span className="barcode-print__band-text barcode-print__band-text--bold">MRN:</span>
              <span className="barcode-print__band-text">{patient.mrn}</span>
            </div>
            {patient.patientId && patient.patientId !== '--' && (
              <div className="barcode-print__band-id">
                <span className="barcode-print__band-id-number">{patient.patientId}</span>
                {renderDataMatrix(patient.patientId, 'barcode-print__band-matrix')}
              </div>
            )}
          </div>
          {/* Connector from tag to bottom strap */}
          <div className="barcode-print__band-connector" />
          {/* Bottom strap with punch holes */}
          <div className="barcode-print__band-strap-bottom">
            {punchHoles.map((_, i) => (
              <div key={`punch-${i}`} className="barcode-print__band-punch" />
            ))}
          </div>
        </div>

        {/* Right: red allergy wristband (only when patient has allergies) */}
        {hasAllergies && (
          <div className="barcode-print__wristband barcode-print__wristband--allergy">
            <div className="barcode-print__band-strap-top barcode-print__band-strap-top--allergy">
              <div className="barcode-print__band-clip-hole barcode-print__band-clip-hole--allergy" />
            </div>
            <div className="barcode-print__band-connector barcode-print__band-connector--allergy" />
            <div className="barcode-print__band-tag barcode-print__band-tag--allergy">
              <div className="barcode-print__band-info">
                <span className="barcode-print__band-text barcode-print__band-text--bold">{patient.name}</span>
                <span className="barcode-print__band-text barcode-print__band-text--bold">{patient.age} Y</span>
                {patient.dateOfBirth && <span className="barcode-print__band-text barcode-print__band-text--bold">{patient.dateOfBirth} DOB</span>}
                <span className="barcode-print__band-text barcode-print__band-text--bold">MRN:</span>
                <span className="barcode-print__band-text">{patient.mrn}</span>
              </div>
              <div className="barcode-print__band-allergy-info">
                <span className="barcode-print__band-allergy-label">Allergies:</span>
                <span className="barcode-print__band-allergy-value">{patient.allergies}</span>
              </div>
            </div>
            <div className="barcode-print__band-connector barcode-print__band-connector--allergy" />
            <div className="barcode-print__band-strap-bottom barcode-print__band-strap-bottom--allergy">
              {punchHoles.map((_, i) => (
                <div key={`allergy-punch-${i}`} className="barcode-print__band-punch barcode-print__band-punch--allergy" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};
